import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import './DashboardWelcomePanel.css';

type DashboardWelcomePanelProps = {
  betfairConnected: boolean;
  loading: boolean;
};

const DashboardWelcomePanel: React.FC<DashboardWelcomePanelProps> = ({ betfairConnected, loading }) => {
  const [expanded, setExpanded] = useState(() => !betfairConnected);

  useEffect(() => {
    if (loading) return;
    setExpanded(!betfairConnected);
  }, [loading, betfairConnected]);

  const toggle = () => setExpanded((e) => !e);

  if (loading) {
    return null;
  }

  return (
    <section className="dashboard-welcome" aria-labelledby="dashboard-welcome-heading">
      <div className="dashboard-welcome-card">
        <button
          type="button"
          className="dashboard-welcome-toggle"
          onClick={toggle}
          aria-expanded={expanded}
          aria-controls="dashboard-welcome-body"
          id="dashboard-welcome-heading"
        >
          <span className="dashboard-welcome-title">Getting started</span>
          <ChevronDown size={20} className={`dashboard-welcome-chevron${expanded ? ' is-open' : ''}`} aria-hidden />
        </button>

        <div
          id="dashboard-welcome-body"
          className={`dashboard-welcome-body${expanded ? ' is-open' : ''}`}
          aria-hidden={!expanded}
        >
          <p className="dashboard-welcome-lead">
            Welcome to GallopAI. Use the bullets below to get oriented, then the top navigation to move around the app. For full detail, see the{' '}
            <Link to="/guide/getting-started">user guide</Link>.
          </p>

          <h3 className="dashboard-welcome-sub">Your first steps</h3>
          <ul className="dashboard-welcome-bullets">
            <li>
              <strong>Connect Betfair</strong> on this page so you can place exchange bets from GallopAI (required for AI Mode and in-app placement).
            </li>
            <li>
              Set your <strong>stake per point</strong> and Betfair commission rate in <Link to="/settings">Settings</Link> before turning on AI Mode.
            </li>
            <li>
              Open <Link to="/today-races">Today&apos;s Races</Link> to review picks and analysis when there are qualifying races.
            </li>
            <li>
              Use <Link to="/performance">Performance</Link> and <Link to="/results">Results History</Link> to review your record over time.
            </li>
            <li>
              <strong>Need Support?</strong> Speak to the Guv&apos;Nor using the chat bubble in the bottom right of the page.
            </li>
          </ul>

          <h3 className="dashboard-welcome-sub">Download mobile app</h3>
          <ol className="dashboard-welcome-bullets dashboard-welcome-bullets-ordered">
            <li>Open GallopAI in your mobile browser.</li>
            <li>In the browser menu, choose &lsquo;Add to Home Screen&rsquo;.</li>
            <li>Install GallopAI.</li>
            <li>
              Enable push notifications in your <Link to="/settings">Settings</Link>.
            </li>
          </ol>

          <p className="dashboard-welcome-help">
            More help: <Link to="/guide/getting-started">User guide</Link>
            {' · '}
            <Link to="/faq">FAQ</Link>
            {' · '}
            <Link to="/contact">Contact</Link>
          </p>
        </div>
      </div>
    </section>
  );
};

export default DashboardWelcomePanel;
